<template>
    <div class="site-permissions p-6">
        <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h2 class="text-xl font-semibold text-gray-800">Site Permissions</h2>
            <div class="w-full md:w-64">
                <div class="relative">
                    <input
                        v-model="searchQuery"
                        type="text"
                        placeholder="Search sites..."
                        class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    >
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd" />
                        </svg>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="loading" class="flex justify-center items-center py-12">
            <div class="flex flex-col items-center">
                <svg class="animate-spin h-8 w-8 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p class="mt-2 text-gray-600">Loading permissions...</p>
            </div>
        </div>

        <div v-else-if="error" class="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="ml-3">
                    <p class="text-sm text-red-700">{{ error }}</p>
                </div>
            </div>
        </div>

        <!-- Global Permissions Section -->
        <div v-if="!loading && globalPermissionTypes.length > 0" class="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg mb-6">
            <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 class="text-lg font-medium text-gray-900">Global Permissions</h3>
                <p class="mt-1 text-sm text-gray-500">These permissions apply across all sites</p>
            </div>
            <div class="px-6 py-4">
                <div class="flex flex-wrap gap-4">
                    <div 
                        v-for="permission in globalPermissionTypes" 
                        :key="permission.id"
                        class="flex items-center"
                    >
                        <input
                            :id="`global-${permission.name}`"
                            type="checkbox"
                            class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            :checked="hasGlobalPermission(permission.name)"
                            @change="updateGlobalPermission(permission.name, $event.target.checked)"
                            :disabled="saving"
                        />
                        <label :for="`global-${permission.name}`" class="ml-2 text-sm text-gray-700">
                            {{ permission.display_name || permission.name }}
                        </label>
                    </div>
                </div>
            </div>
        </div>

        <!-- Entity-Level Permissions Table -->
        <div v-if="!loading && !error" class="bg-white shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
            <div class="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 class="text-lg font-medium text-gray-900">Site-Specific Permissions</h3>
                <p class="mt-1 text-sm text-gray-500">These permissions apply to specific sites</p>
            </div>
            <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                        <tr>
                            <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                                Site
                            </th>
                            <th 
                                v-for="permission in entityLevelPermissionTypes" 
                                :key="permission.id"
                                scope="col" 
                                class="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider"
                                :title="permission.description"
                            >
                                {{ permission.display_name || permission.name }}
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                        <template v-if="filteredSites.length === 0">
                            <tr>
                                <td colspan="100" class="px-6 py-4 text-center text-sm">
                                    No sites found
                                </td>
                            </tr>
                        </template>
                        <template v-else>
                            <tr v-for="site in filteredSites" :key="site.id" class="hover:bg-gray-50">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-medium">{{ site.name }}</div>
                                    <div v-if="site.building" class="text-xs text-gray-500">
                                        {{ site.building.address_line_1 }}, {{ site.building.city }}, {{ site.building.state }} {{ site.building.zip_code }}
                                    </div>
                                </td>
                                <td 
                                    v-for="permission in entityLevelPermissionTypes" 
                                    :key="permission.id"
                                    class="px-6 py-4 whitespace-nowrap text-center text-sm"
                                >
                                    <input
                                        type="checkbox"
                                        :checked="hasPermission(site.id, permission.name)"
                                        @change="updatePermission(site, permission.name, $event.target.checked)"
                                        class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                        :disabled="saving"
                                    />
                                </td>
                            </tr>
                        </template>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="!loading" class="mt-6 flex justify-between items-center">
            <div class="text-sm text-gray-500">
                Showing {{ filteredSites.length }} of {{ sites.length }} sites
            </div>
            <button
                @click="saveAllPermissions"
                :disabled="saving"
                class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <svg v-if="saving" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'SitePermissions',
    props: {
        userId: {
            type: [Number, String],
            required: true
        }
    },
    data() {
        return {
            sites: [],
            permissionTypes: [],
            loading: false,
            saving: false,
            error: null,
            searchQuery: '',
            permissionGroup: 'site',
            userPermissions: {},
            globalPermissions: []
        }
    },
    async created() {
        this.fetchData();
    },
    computed: {
        filteredSites() {
            if (!this.searchQuery) {
                return this.sites;
            }
            const query = this.searchQuery.toLowerCase();
            return this.sites.filter(site => 
                site.name.toLowerCase().includes(query) ||
                (site.building?.address_line_1 || '').toLowerCase().includes(query) ||
                (site.building?.city || '').toLowerCase().includes(query) ||
                (site.building?.state || '').toLowerCase().includes(query) ||
                (site.building?.zip_code || '').toLowerCase().includes(query)
            );
        },
        globalPermissionTypes() {
            return this.permissionTypes.filter(pt => pt.is_global === true);
        },
        entityLevelPermissionTypes() {
            return this.permissionTypes.filter(pt => pt.is_global === false);
        }
    },
    methods: {
        hasPermission(siteId, permissionName) {
            return this.userPermissions[siteId] && Array.isArray(this.userPermissions[siteId]) 
                ? this.userPermissions[siteId].includes(permissionName) 
                : false;
        },
        
        hasGlobalPermission(permissionName) {
            return this.globalPermissions.includes(permissionName);
        },
        
        async fetchData() {
            this.loading = true;
            this.error = null;
            
            try {
                // Fetch sites with their permissions
                const [sitesRes, permissionsRes] = await Promise.all([
                    axios.get('/api/sites'),
                    this.userId ? axios.get(`/api/users/${this.userId}/permissions/${this.permissionGroup}`) : Promise.resolve({ data: { data: [] } })
                ]);
                
                this.sites = sitesRes.data;
                
                // Transform the permissions into the expected format
                this.userPermissions = {};
                
                // The actual data is nested under data.data
                const responseData = permissionsRes.data?.data || {};
                const items = responseData.items || [];
                const groupData = responseData.group || {};
                
                // Store permission types
                this.permissionTypes = groupData.permission_types || [];
                
                // Process permissions
                this.globalPermissions = [];
                if (Array.isArray(items)) {
                    items.forEach(item => {
                        const sitePermissions = [];
                        
                        // If permissions is an object with boolean values
                        if (item.permissions && typeof item.permissions === 'object') {
                            Object.entries(item.permissions).forEach(([permName, hasPerm]) => {
                                if (hasPerm) {
                                    if (item.id === null) {
                                        // This is a global permission
                                        this.globalPermissions.push(permName);
                                    } else {
                                        // This is a site-specific permission
                                        sitePermissions.push(permName);
                                    }
                                }
                            });
                        }
                        
                        if (item.id !== null) {
                            this.$set(this.userPermissions, item.id, sitePermissions);
                        }
                    });
                }
                
            } catch (error) {
                console.error('Error fetching site permissions:', error);
                this.error = 'Failed to load site permissions. Please try again later.';
            } finally {
                this.loading = false;
            }
        },
        
        updatePermission(site, permissionName, hasPermission) {
            if (!this.userPermissions[site.id]) {
                this.$set(this.userPermissions, site.id, []);
            }
            
            if (hasPermission) {
                if (!this.userPermissions[site.id].includes(permissionName)) {
                    this.userPermissions[site.id].push(permissionName);
                }
            } else {
                const index = this.userPermissions[site.id].indexOf(permissionName);
                if (index > -1) {
                    this.userPermissions[site.id].splice(index, 1);
                }
            }
        },
        
        updateGlobalPermission(permissionName, hasPermission) {
            if (hasPermission) {
                if (!this.globalPermissions.includes(permissionName)) {
                    this.globalPermissions.push(permissionName);
                }
            } else {
                const index = this.globalPermissions.indexOf(permissionName);
                if (index > -1) {
                    this.globalPermissions.splice(index, 1);
                }
            }
        },
        
        async saveAllPermissions() {
            this.saving = true;
            this.error = null;
            
            try {
                // Group permissions by site
                const permissionsBySite = {};
                
                Object.entries(this.userPermissions).forEach(([siteId, permissions]) => {
                    const site = this.sites.find(s => s.id == siteId);
                    if (site) {
                        const permissionObject = {};
                        this.entityLevelPermissionTypes.forEach(type => {
                            permissionObject[type.name] = permissions.includes(type.name);
                        });
                        
                        permissionsBySite[siteId] = permissionObject;
                    }
                });
                
                // Save permissions for each site
                const promises = Object.entries(permissionsBySite).map(([siteId, permissions]) => {
                    return axios.post(`/api/users/${this.userId}/permissions`, {
                        permissionable_type: 'site',
                        permissionable_id: siteId,
                        permissions: permissions
                    });
                });
                
                // Save global permissions
                const globalPermissionObject = {};
                this.globalPermissionTypes.forEach(type => {
                    globalPermissionObject[type.name] = this.globalPermissions.includes(type.name);
                });
                
                promises.push(
                    axios.post(`/api/users/${this.userId}/permissions`, {
                        permissionable_type: 'site',
                        permissionable_id: null, // Indicates global permission
                        permissions: globalPermissionObject
                    })
                );
                
                await Promise.all(promises);
                
                // Show success message
                this.$toast.success('Site permissions updated successfully');
                
            } catch (error) {
                console.error('Error saving site permissions:', error);
                this.error = 'Failed to save site permissions. Please try again.';
                this.$toast.error('Failed to save site permissions');
            } finally {
                this.saving = false;
            }
        }
    }
}
</script>
